import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewEncapsulation, computed, effect, inject, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { BusinessConfigService } from '../../services/business-config.service';
import { PayoutService } from '../../services/payout.service';
import { PrivacyPolicyService } from '../../services/privacy-policy.service';
import { KuboComponent, KuboRecommendation, KuboState } from '../ui/kubo.component';
import { KuboAvatarComponent } from '../ui/kubo-avatar.component';
import { KuboService, KuboTarget } from '../../services/kubo.service';

export type OnboardingModule = 'overview'|'configuracion'|'ingresosNegocio'|'salas'|'reservas'|'calendario'|'usuarios'|'estadisticas'|'reciclaje'|'subadministrador'|'novedades'|'suscripcion'|'superadmin';
interface TourStep { module: OnboardingModule; icon: string; title: string; text: string; }
type WalkStepId = 'company-info'|'logo'|'policies'|'cancellation'|'payout';
interface WalkStep { id: WalkStepId; module: OnboardingModule; tabHash?: string; done(): boolean; flashMessage?: string; }

@Component({
  selector: 'app-owner-onboarding', standalone: true, imports: [CommonModule, KuboComponent, KuboAvatarComponent],
  template: `
    <app-kubo *ngIf="kuboVisible()" [recommendations]="kuboRecommendations()" storageKey="onboarding-kubo"></app-kubo>
    <section *ngIf="showSetup()" class="onboarding-overlay kubo-intro" role="dialog" aria-modal="true">
      <div class="setup-card kubo-intro-card">
        <app-kubo-avatar class="kubo-intro-avatar" mood="saludo" [animated]="true" [shadow]="true"></app-kubo-avatar>
        <div class="kubo-intro-bubble" aria-live="polite">{{ introBubbleText() }}</div>
        <h2>Preparemos tu panel</h2>
        <p>{{ introSummary() }}</p>
        <div class="setup-importance"><i class="bi bi-shield-check"></i><span>Puedes omitirlo por ahora, pero tu panel no estará listo para operar hasta completar ambos apartados.</span></div>
        <div class="setup-actions"><button class="later" type="button" (click)="skipSetup()">Omitir por ahora</button><button class="continue" type="button" (click)="beginWalkthrough()">Iniciar</button></div>
      </div>
    </section>
    <section *ngIf="tourActive()" class="context-tour" role="dialog" aria-modal="false" aria-live="polite">
      <div class="tour-veil"></div>
      <div class="tour-card contextual"><header><span>{{tourIndex()+1}} de {{currentSteps().length}}</span><button type="button" aria-label="Omitir tutorial" (click)="finishTour(true)"><i class="bi bi-x-lg"></i></button></header><app-kubo-avatar class="tour-icon kubo-mini-avatar" mood="explicando" [animated]="true"></app-kubo-avatar><span class="tour-kicker">KUBO TE GUÍA POR EL SISTEMA</span><h2>{{step().title}}</h2><p>{{step().text}}</p><small class="tour-location"><i class="bi bi-cursor-fill"></i> El módulo ya está abierto para que puedas reconocerlo</small><div class="tour-progress"><i [style.width.%]="((tourIndex()+1)/currentSteps().length)*100"></i></div><footer><button type="button" [disabled]="tourIndex()===0" (click)="previous()">Anterior</button><button class="next" type="button" (click)="next()">{{tourIndex()===currentSteps().length-1?'Finalizar':'Siguiente'}}</button></footer></div>
    </section>
    <section *ngIf="welcome()" class="welcome-finale" aria-live="polite"><div class="welcome-aurora a"></div><div class="welcome-aurora b"></div><div class="welcome-stars"><i></i><i></i><i></i><i></i><i></i></div><div class="welcome-content"><div class="welcome-emblem"><span class="emblem-ring one"></span><span class="emblem-ring two"></span><i class="bi bi-door-open"></i></div><span class="welcome-kicker">BIENVENIDO A TUSALA</span><h1>Tu espacio. Tu negocio.<br><b>Un nuevo comienzo.</b></h1><p>Todo está preparado para que administres, conectes y hagas crecer cada oportunidad.</p><div class="welcome-pillars"><span><i class="bi bi-shield-check"></i> Control</span><span><i class="bi bi-graph-up-arrow"></i> Crecimiento</span><span><i class="bi bi-stars"></i> Experiencias</span></div><div class="welcome-loading"><i></i></div></div></section>`,
  styleUrls: ['./owner-onboarding.component.css','./guided-system-tour.component.css','./welcome-experience.component.css','./onboarding-flow.component.css'],
  styles: [`
    .context-tour{position:fixed!important;z-index:100000!important;inset:0;pointer-events:none;isolation:isolate}
    .context-tour .tour-veil{position:absolute;z-index:0;inset:0;background:var(--ts-bg-navy-950-a42-2);backdrop-filter:blur(1px)}
    .context-tour .tour-card.contextual{position:absolute!important;z-index:2!important;top:clamp(82px,11vh,118px)!important;right:clamp(18px,3vw,52px)!important;bottom:auto!important;left:auto!important;width:min(390px,calc(100vw - 36px))!important;max-height:calc(100dvh - 150px)!important;pointer-events:auto;border-radius:20px;animation:tourBreathing 3.8s ease-in-out infinite!important}
    .context-tour .tour-card.contextual::before{display:none!important}
    .context-tour .tour-veil{background:var(--ts-bg-navy-950-a4-2)!important;backdrop-filter:none!important}
    @keyframes tourBreathing{0%,100%{transform:translateY(0);box-shadow:0 22px 65px var(--ts-shadow-gray-1000-a44)}50%{transform:translateY(-3px);box-shadow:0 28px 76px var(--ts-shadow-sky-600-a32)}}
    @media(max-width:900px) and (min-width:641px){.context-tour .tour-card.contextual{top:86px!important;right:16px!important;bottom:auto!important;left:auto!important;width:min(350px,calc(100vw - 32px))!important;max-height:calc(100dvh - 118px)!important}}
    @media(max-width:640px){.context-tour .tour-card.contextual{top:auto!important;right:10px!important;bottom:max(10px,env(safe-area-inset-bottom))!important;left:10px!important;width:auto!important;max-height:min(43dvh,300px)!important}}
    @media(prefers-reduced-motion:reduce){.context-tour .tour-card.contextual{animation:none!important}}
    .welcome-finale div{animation:none}
    .welcome-finale .welcome-content{animation:welcomeContent 1s cubic-bezier(.2,.8,.2,1) both}
    .welcome-finale .welcome-aurora{animation:welcomeFloat 3s ease-in-out infinite alternate}
    .welcome-finale .welcome-aurora.b{animation-delay:-1.5s}
    .welcome-finale .welcome-emblem>i{display:grid!important;width:76px!important;height:76px!important;margin:0!important;padding:0!important;border-radius:23px!important;background:linear-gradient(145deg,var(--ts-bg-sky-450-5),var(--ts-bg-indigo-350-11))!important;font-size:1.9rem!important}
    .welcome-finale .welcome-stars i{position:absolute!important;display:block!important;width:4px!important;height:4px!important;margin:0!important;padding:0!important;border:0!important;border-radius:50%!important;background:var(--ts-bg-blue-150)!important;box-shadow:0 0 12px var(--ts-shadow-sky-300)!important;animation:welcomeStar 1.8s ease-in-out infinite!important}
    .welcome-finale .welcome-pillars i{display:inline-block!important;width:auto!important;height:auto!important;margin:0!important;padding:0!important;border:0!important;border-radius:0!important;background:none!important;box-shadow:none!important;font-size:.8rem!important}
    .welcome-finale .welcome-loading i{display:block!important;width:100%!important;height:100%!important;margin:0!important;padding:0!important;border:0!important;border-radius:inherit!important;background:linear-gradient(90deg,var(--ts-bg-sky-450-6),var(--ts-bg-indigo-350-12),var(--ts-bg-teal-400))!important;box-shadow:none!important;animation:welcomeLoad 2.6s linear both!important}
    @media(max-width:640px){.welcome-finale .welcome-emblem>i{width:62px!important;height:62px!important;border-radius:19px!important;font-size:1.55rem!important}}
    @media(max-height:620px){.welcome-finale .welcome-emblem>i{width:52px!important;height:52px!important;font-size:1.3rem!important}}
    @media(prefers-reduced-motion:reduce){.welcome-finale *{animation:none!important}}

    /* Kubo (app-kubo-avatar) en el modal inicial y en el recorrido: el mismo
       personaje del asistente, más grande en su primera aparición. */
    .kubo-intro-avatar{display:block;width:124px;height:124px;margin:0 auto 6px}
    .tour-icon.kubo-mini-avatar{display:block!important;width:72px!important;height:72px!important;margin:.4rem auto .2rem!important;padding:0!important;border:0!important;border-radius:0!important;background:none!important;box-shadow:none!important}
    .kubo-intro-bubble{position:relative;max-width:min(320px,84vw);margin:0 auto 16px;padding:.55rem .9rem;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:999px;background:var(--ts-surface-soft,#102640);font-size:.78rem;font-weight:700;text-align:center;animation:kuboBubbleIn .22s ease both}
    @keyframes kuboBubbleIn{from{opacity:0;transform:translateY(4px)}}
    @media(prefers-reduced-motion:reduce){.kubo-intro-bubble{animation:none}}
  `],
  encapsulation: ViewEncapsulation.None,
})
export class OwnerOnboardingComponent implements OnChanges, OnDestroy {
  @Input({required:true}) businessId=0; @Input() userId=0; @Input() userEmail=''; @Input() role=''; @Input() plan=''; @Input() firstLogin=false; @Input() required=false; @Input() configurationExempt=false;
  @Input() businessName=''; @Input() ownerName='';
  // string (no OnboardingModule) a propósito: dashboard.component.ts tiene
  // su propio DashboardTab con valores que OnboardingModule no cubre
  // (p. ej. 'superadminNovedades') — acá solo se compara por igualdad
  // contra los 2 módulos que le interesan al recorrido guiado, así que un
  // tipo más ancho evita acoplar los dos tipos innecesariamente.
  @Input() currentModule: string|null=null;
  @Output() navigate=new EventEmitter<OnboardingModule>();
  readonly hasCompanyInfo=signal(false); readonly hasLogo=signal(false); readonly hasActivePolicy=signal(false); readonly hasCancellationPolicy=signal(false); readonly payoutComplete=signal(false);
  readonly showSetup=signal(false); readonly tourActive=signal(false); readonly tourIndex=signal(0); readonly welcome=signal(false);
  readonly walkthroughActive=signal(false);
  readonly setupStateReady=signal(false);
  readonly miSitioVisited=signal(false); readonly tourCompleted=signal(false);
  // Una vez que el recorrido guiado de onboarding (modal -> pasos/oferta de
  // tour -> tour -> bienvenida) concluye POR PRIMERA VEZ, Kubo se despide.
  // A diferencia de la versión anterior, esto ya NO es un cerrojo de una
  // sola vía: si más adelante el usuario elimina alguna configuración
  // necesaria (p. ej. borra el teléfono del negocio o su único método de
  // cobro), Kubo debe reactivarse — ver kuboVisible()/allComplete() más
  // abajo, que vuelven a mostrar a Kubo aunque este flag siga en true.
  // Persistido para sobrevivir recargas. Distinto de tourCompleted(): una
  // cuenta que nunca pasó por el modal de onboarding (ni required ni
  // firstLogin) puede seguir viendo recomendaciones sueltas de Kubo con
  // normalidad.
  readonly onboardingGuideFinished=signal(false);
  // True desde el momento en que se omite el modal inicial (se ofrece de
  // inmediato) HASTA que se completan naturalmente los pasos guiados
  // (Iniciar) — en ambos casos termina mostrando la oferta de recorrido
  // con sus dos botones ("Iniciar ahora" / "Omitir recorrido"). Persistido
  // para que sobreviva una recarga a medio decidir.
  readonly tourOfferPending=signal(false);
  // Secuencia breve y no bloqueante que se muestra justo al completar la
  // información pendiente (ya sea la primera vez tras omitir/recordatorio,
  // o al corregir una regresión): un resumen tipo checklist con un
  // indicador de "procesando" y, después, la despedida — ver
  // concludeOnboarding().
  readonly finishingUpStage=signal<'checklist'|'goodbye'|null>(null);
  private finishStageTimer?:ReturnType<typeof setTimeout>;
  // Detecta la transición real de "falta algo" -> "todo completo" (para
  // decidir si corresponde ofrecer el tour u ofrecer el cierre) sin
  // disparar nada en la primera observación (evita un falso positivo si
  // el usuario ya tenía todo completo de antes al montar el componente).
  private prevAllDone=false;
  private stepTrackingInitialized=false;
  readonly configHash=signal(typeof window!=='undefined'?window.location.hash.replace(/^#/,''):'');
  // Puente para que kuboRecommendations()/profileComplete() (computed) se
  // revaliden también cuando cambian @Input planos (plan/configurationExempt/
  // userEmail/businessName/ownerName/currentModule) que no son señales —
  // sin esto quedarían "congelados" en el primer valor recibido.
  private readonly inputsTick=signal(0);
  // Mensaje transitorio de "paso recién completado" (logo/políticas/cobro):
  // vive acá (no dentro de Kubo, que es puramente presentacional) y se
  // activa cuando cada paso pasa de pendiente a completo — ver refresh().
  readonly justCompletedStep=signal<WalkStepId|null>(null);
  private readonly prevStepDone: Record<WalkStepId, boolean> = { 'company-info': false, 'logo': false, 'policies': false, 'cancellation': false, 'payout': false };
  private stepFlashTimer?:ReturnType<typeof setTimeout>;
  private setupDismissed=false; private initialTour=false; private displayedModule?:OnboardingModule;
  private readonly defaultTabHash: Partial<Record<OnboardingModule,string>> = { configuracion:'informacion-negocio', ingresosNegocio:'metodos-de-cobro' };
  // Frases genéricas para las burbujas flotantes del recordatorio (no
  // mencionan el campo puntual: eso lo dice el detalle al abrir el
  // cuadro) — mismo espíritu que las de app-help-widget.
  private readonly reminderBubbles=['Tienes cosas por completar','¿Puedo ayudarte en algo?','Completa la información de tu panel','Estoy aquí para ayudarte','Puedo guiarte'];
  readonly steps:TourStep[]=[
    {module:'overview',icon:'bi bi-grid-1x2',title:'Tu centro de control',text:'Consulta el resumen general y los accesos rápidos de tu operación.'},
    {module:'salas',icon:'bi bi-door-open',title:'Gestión de salas',text:'Administra el catálogo, disponibilidad, publicación y presentación de tus espacios.'},
    {module:'reservas',icon:'bi bi-calendar2-check',title:'Reservas',text:'Consulta solicitudes, estados, pagos y acciones operativas de cada reserva.'},
    {module:'calendario',icon:'bi bi-calendar3',title:'Calendario',text:'Visualiza la agenda real de tus espacios y evita cruces de disponibilidad.'},
    {module:'usuarios',icon:'bi bi-people',title:'Clientes',text:'Consulta el directorio, actividad y estado de los clientes de tu negocio.'},
    {module:'estadisticas',icon:'bi bi-bar-chart-line',title:'Estadísticas',text:''},
    {module:'reciclaje',icon:'bi bi-trash3',title:'Papelera',text:'Revisa los elementos eliminados temporalmente y restaura los que todavía se encuentren disponibles.'},
    {module:'configuracion',icon:'bi bi-building-gear',title:'Configuración',text:'Mantén actualizados los datos, contactos, logo, seguridad y políticas de tu negocio.'},
    {module:'ingresosNegocio',icon:'bi bi-wallet2',title:'Ingresos y transferencias',text:'Define cómo recibir tus ingresos y consulta la actividad financiera registrada.'},
    {module:'subadministrador',icon:'bi bi-person-gear',title:'Subadministradores',text:'Incorpora colaboradores y asigna únicamente los permisos que necesitan.'},
    {module:'novedades',icon:'bi bi-megaphone',title:'Novedades',text:'Consulta confirmaciones, avisos y eventos relevantes de la operación.'},
    {module:'suscripcion',icon:'bi bi-gem',title:'Mi suscripción',text:'Consulta tu plan, beneficios, vigencia, pagos y opciones de crecimiento.'},
  ];
  private readonly superAdminSteps:TourStep[]=[
    {module:'overview',icon:'bi bi-grid-1x2',title:'Tu centro de control',text:'Consulta la operación del negocio asociado.'},
    ...this.steps.slice(1),
    {module:'superadmin',icon:'bi bi-shield-check',title:'Centro de operaciones',text:'Administra propietarios, planes, suscripciones, pagos y métricas globales.'},
  ];
  private readonly kubo=inject(KuboService);
  // Lugar exacto de cada paso: al tocar "Ir", Kubo lleva al módulo y además
  // señala el campo o botón donde se completa (en orden de preferencia).
  private readonly stepTargets:Record<WalkStepId,KuboTarget&{title:string;text:string}>={
    // Los datos se editan en un modal: si está cerrado, Kubo señala «Editar» y,
    // al tocarlo, el campo vacío exacto dentro del modal.
    'company-info':{selector:'.field-company, .field-phone',firstEmpty:true,label:'Completa este dato',fallback:{selector:'#panel-informacion-negocio .identity-card-head .btn-edit',label:'Toca «Editar» para completar',next:{selector:'.field-company, .field-phone',firstEmpty:true,label:'Completa este dato'}},title:'Aquí van los datos de tu negocio',text:'Toca «Editar», escribe la razón social y el teléfono, y guarda los cambios.'},
    'logo':{selector:'#panel-identidad-visual .logo-upload .file-label, #panel-identidad-visual .logo-section',label:'Sube tu logo aquí',title:'Sube el logo de tu negocio',text:'Elige la imagen y luego presiona «Guardar logo». Se verá en tu sitio y en los correos.'},
    'policies':{selector:'#panel-politicas-negocio .policies-table .btn-activate, #panel-politicas-negocio .section-header .btn-edit',label:'Completa tu política aquí',next:{selector:'input#title',label:'Empieza por el nombre'},title:'Tu política de privacidad',text:'Crea tu política (o activa una que ya tengas) para que tus clientes la vean antes de reservar.'},
    'cancellation':{selector:'app-cancellation-policy-settings .cp-card',label:'Elige tu política aquí',title:'Política de cancelación',text:'Indica con cuántas horas de anticipación pueden cancelar y si cobras un cargo por reembolso; luego guarda.'},
    'payout':{selector:'#panel-metodos-de-cobro .section-heading-actions .primary-button',label:'Agrega tu cuenta aquí',title:'Registra tu cuenta de cobro',text:'Presiona «Agregar método» y completa los datos de la cuenta donde recibirás tus ingresos.'},
  };
  private readonly stepLabels:Record<WalkStepId,{title:string;description:string}>={
    'company-info':{title:'Información del negocio',description:'Nombre comercial y teléfono de contacto.'},
    'logo':{title:'Logo',description:'Se muestra en tu sitio público y en los correos.'},
    'policies':{title:'Política de privacidad',description:'Visible para tus clientes antes de reservar.'},
    'cancellation':{title:'Política de cancelación',description:'Anticipación para cancelar y cargo por reembolso.'},
    'payout':{title:'Método de cobro',description:'Cuenta donde TuSala te transfiere tus ingresos.'},
  };
  constructor(private business:BusinessConfigService,private payouts:PayoutService,private policies:PrivacyPolicyService,private auth:AuthService){
    // Kubo global: se oculta mientras el modal inicial, el recorrido o la
    // bienvenida ocupan la escena (ahí Kubo ya está dentro de esa pantalla).
    effect(()=>this.kubo.suppressed.set(this.showSetup()||this.tourActive()||this.welcome()));
    // Progreso de configuración en el panel de Kubo (datos reales, con "Ir").
    effect(()=>{
      this.inputsTick();
      if(!this.setupStateReady()||!this.isEligible()){this.kubo.progress.set(null);return;}
      const steps=(this.configurationRequired()?this.walkSteps():this.walkSteps().filter(s=>s.id==='payout')).map(step=>({key:step.id,...this.stepLabels[step.id],done:step.done(),go:()=>this.goToStep(step)}));
      this.kubo.progress.set({title:'Configura tu panel',steps});
    });
  }
  ngOnChanges(c:SimpleChanges){
    if((c['businessId']||c['role']||c['userEmail']||c['configurationExempt'])&&this.isEligible())this.refresh();
    if(c['plan']||c['configurationExempt']||c['userEmail']||c['businessName']||c['ownerName']||c['currentModule']||c['role']||c['businessId'])this.inputsTick.update(v=>v+1);
  }
  ngOnDestroy(){if(this.stepFlashTimer)clearTimeout(this.stepFlashTimer);if(this.finishStageTimer)clearTimeout(this.finishStageTimer);this.stopIntroBubbles();this.kubo.progress.set(null);this.kubo.suppressed.set(false)}
  @HostListener('window:tusala:payout-methods-changed') onPayoutMethodsChanged(){this.refresh()}
  @HostListener('window:tusala:business-configuration-changed') onConfigurationChanged(){this.refresh()}
  @HostListener('window:tusala:start-tutorial') onStartTutorial(){if(this.isEligible())this.startTour()}
  @HostListener('window:hashchange') onHashChange(){this.configHash.set(window.location.hash.replace(/^#/,''))}
  @HostListener('window:tusala:mi-sitio-visited') onMiSitioVisited(){
    this.miSitioVisited.set(true);
    localStorage.setItem(this.key('setup-visited:mi-sitio'),'1');
  }
  canUseOwnSite(){return this.configurationExempt||['estandar','premium','gratis'].includes(String(this.plan||'').trim().toLowerCase())}
  configurationRequired(){return !this.configurationExempt&&String(this.userEmail||'').trim().toLowerCase()!=='admin@tusala.net'}
  profileComplete=computed<boolean>(()=>{this.inputsTick();return !this.configurationRequired()||(this.hasCompanyInfo()&&this.hasLogo()&&this.hasActivePolicy()&&this.hasCancellationPolicy())});
  // Los DOS módulos que el flujo exige: configuración (3 partes) + cobro.
  allComplete=computed<boolean>(()=>{this.inputsTick();return this.profileComplete()&&this.payoutComplete()});
  // Gate único de montaje de <app-kubo>: además de las condiciones de
  // siempre (listo, elegible, sin modal/tour/bienvenida encima), Kubo
  // puede volver a aparecer aunque ya se haya despedido antes — mientras
  // dura la secuencia de cierre (checklist/despedida) o si algo que ya
  // estaba completo dejó de estarlo (regresión, ver clase comment de
  // onboardingGuideFinished).
  kuboVisible=computed<boolean>(()=>{
    this.inputsTick();
    if(!this.setupStateReady()||!this.isEligible())return false;
    if(this.showSetup()||this.tourActive()||this.welcome())return false;
    if(this.finishingUpStage())return true;
    if(!this.onboardingGuideFinished())return true;
    return !this.allComplete();
  });
  // Cubre cualquier forma en la que el usuario ya interactuó con el modal
  // inicial o con la oferta de tour, en esta sesión o en una anterior
  // (persistido) — usado para no disparar la lógica de "recién completó
  // todo" en un usuario que nunca pasó por el modal (ej. cuenta exenta).
  private engagedWithOnboarding():boolean{
    return this.setupDismissed||this.setupSkipped()||this.walkthroughStarted()||this.tourDecided()||this.tourOfferPending()||this.onboardingGuideFinished();
  }

  private walkSteps():WalkStep[]{
    return [
      {id:'company-info',module:'configuracion',tabHash:'informacion-negocio',done:()=>this.hasCompanyInfo()},
      {id:'logo',module:'configuracion',tabHash:'informacion-negocio',done:()=>this.hasLogo(),flashMessage:'¡Logo actualizado!'},
      {id:'policies',module:'configuracion',tabHash:'politicas-negocio',done:()=>this.hasActivePolicy(),flashMessage:'¡Política de privacidad activa!'},
      // Cada negocio elige su anticipación para cancelar y si cobra un cargo
      // administrativo en reembolsos (antes eran 6 h fijas para todos).
      {id:'cancellation',module:'configuracion',tabHash:'politicas-negocio',done:()=>this.hasCancellationPolicy(),flashMessage:'Completamos la información de este módulo.'},
      {id:'payout',module:'ingresosNegocio',tabHash:'metodos-de-cobro',done:()=>this.payoutComplete(),flashMessage:'¡Método de cobro registrado!'},
    ];
  }
  currentWalkStep():WalkStep|null{
    const steps=this.configurationRequired()?this.walkSteps():this.walkSteps().filter(s=>s.id==='payout');
    return steps.find(s=>!s.done())??null;
  }
  private isOnTargetTab(step:WalkStep):boolean{
    if(!step.tabHash)return true;
    const current=this.configHash()||this.defaultTabHash[step.module]||'';
    return current===step.tabHash;
  }
  private showIrFor(step:WalkStep):boolean{return this.currentModule!==step.module||!this.isOnTargetTab(step)}
  private stepContent(id:WalkStepId):{state:KuboState;title:string;message:string}{
    const biz=this.businessName?.trim()||'tu negocio';
    const owner=this.ownerName?.trim();
    const greet=owner?`, ${owner}`:'';
    switch(id){
      case 'company-info':return{state:'recommendation',title:`Revisemos la información de ${biz}`,message:`Verifica los datos y contactos de tu negocio${greet} para que tus clientes confíen en ${biz}.`};
      case 'logo':return{state:'recommendation',title:`Falta el logo de ${biz}`,message:'Súbelo para que se vea profesional en tu sitio público y en los correos que enviamos.'};
      case 'policies':return{state:'recommendation',title:'Completa tus políticas',message:`Define las políticas visibles para tus clientes antes de recibir reservas en ${biz}.`};
      case 'cancellation':return{state:'recommendation',title:'Elige tu política de cancelación',message:`Decide con cuántas horas de anticipación pueden cancelar los clientes de ${biz} y si aplicas un cargo administrativo a los reembolsos. Lo verán en la ficha de cada espacio.`};
      case 'payout':return{state:'recommendation',title:'Registra tu cuenta de cobro',message:`Agrega la cuenta bancaria de ${biz} donde TuSala transferirá, al finalizar cada mes, el dinero acumulado de tus reservas.`};
    }
  }
  private tourOfferRecommendation():KuboRecommendation{
    const owner=this.ownerName?.trim();
    const greet=owner?`, ${owner}`:'';
    const ready=this.allComplete();
    return {
      id:'tour-offer',
      state:'success',
      priority:'critical',
      title:ready?`¡Todo listo${greet}!`:`¡Hola${greet}!`,
      message:ready
        ?'Tu negocio ya está preparado. ¿Hacemos un recorrido rápido por tu panel administrativo?'
        :'Cuando quieras completamos los datos pendientes. Mientras tanto, ¿te gustaría un recorrido rápido para conocer el panel?',
      actionLabel:'Iniciar ahora',
      onAction:()=>this.acceptTourOffer(),
      secondaryActionLabel:'Omitir recorrido',
      onSecondaryAction:()=>this.declineTourOffer(),
    };
  }
  private checklistRecommendation():KuboRecommendation{
    const items:string[]=[];
    if(this.configurationRequired())items.push('Información del negocio','Logo','Políticas','Cancelaciones');
    items.push('Método de cobro');
    return {
      id:'onboarding-checklist',
      state:'success',
      priority:'critical',
      title:'¡Todo completado!',
      message:items.map(i=>`✓ ${i}`).join('   '),
      loading:true,
    };
  }
  private goodbyeRecommendation():KuboRecommendation{
    const owner=this.ownerName?.trim();
    return {
      id:'onboarding-goodbye',
      state:'success',
      priority:'critical',
      title:owner?`¡Nos vemos, ${owner}!`:'¡Nos vemos!',
      message:'Tu panel quedó al día. Me despido por ahora — estaré atento por si algo cambia.',
    };
  }
  // Recomendaciones para Kubo: SOLO arma la lista con estado/mensaje/
  // acción ya resueltos — Kubo no sabe nada de "onboarding", solo
  // muestra la de mayor prioridad. Ramas mutuamente excluyentes (siempre
  // se corta con return apenas hay algo que mostrar) porque solo una
  // recomendación se ve a la vez de todas formas.
  kuboRecommendations=computed<KuboRecommendation[]>(()=>{
    this.inputsTick();
    const stage=this.finishingUpStage();
    if(stage==='checklist')return [this.checklistRecommendation()];
    if(stage==='goodbye')return [this.goodbyeRecommendation()];
    const flashed=this.justCompletedStep();
    if(flashed){
      const flashMsg=this.walkSteps().find(s=>s.id===flashed)?.flashMessage;
      if(flashMsg)return [{id:`flash-${flashed}`,state:'success',priority:'critical',title:'¡Bien hecho!',message:flashMsg}];
    }
    if(this.tourOfferPending())return [this.tourOfferRecommendation()];
    if(!this.allComplete()&&this.engagedWithOnboarding()){
      const step=this.currentWalkStep();
      if(step){
        const content=this.stepContent(step.id);
        return [{
          id:`walk-${step.id}`,
          state:content.state,
          priority:'high',
          title:content.title,
          message:content.message,
          actionLabel:this.showIrFor(step)?'Ir':undefined,
          onAction:this.showIrFor(step)?()=>this.goToStep(step):undefined,
          // Mientras haya algo pendiente, Kubo NO se abre solo — acompaña
          // con burbujas flotantes (como el widget de ayuda del enlace de
          // pago) y solo muestra este detalle si el usuario lo presiona.
          bubbles:this.reminderBubbles,
        }];
      }
      return [];
    }
    if(this.canUseOwnSite()&&!this.miSitioVisited()&&!this.onboardingGuideFinished())return [{
      id:'mi-sitio',
      state:'recommendation',
      priority:'low',
      title:'Personaliza tu sitio público',
      message:'Revisa y comparte el enlace público de tu negocio.',
      actionLabel:'Ir',
      onAction:()=>this.openMiSitio(),
    }];
    return [];
  });
  introBubbleText=computed<string>(()=>{
    this.inputsTick();
    const phrases=this.introPhrases();
    return phrases[this.introBubbleIndex()%phrases.length];
  });
  private introPhrases():string[]{
    const biz=this.businessName?.trim();
    const owner=this.ownerName?.trim();
    return [
      owner?`Hola ${owner}, soy Kubo`:'Hola, soy Kubo',
      biz?`Te ayudaré a configurar el panel para «${biz}»`:'Te ayudaré a configurar tu panel',
      'Iremos paso a paso, sin complicarnos.',
    ];
  }
  private readonly introBubbleIndex=signal(0);
  private introBubbleTimer?:ReturnType<typeof setInterval>;
  private startIntroBubbles(){
    this.stopIntroBubbles();
    this.introBubbleTimer=setInterval(()=>this.introBubbleIndex.update(v=>v+1),2800);
  }
  private stopIntroBubbles(){if(this.introBubbleTimer){clearInterval(this.introBubbleTimer);this.introBubbleTimer=undefined}}
  introSummary=computed<string>(()=>{
    this.inputsTick();
    const biz=this.businessName?.trim()||'tu negocio';
    const parts:string[]=[];
    if(this.configurationRequired())parts.push(`los datos, el logo, las políticas y la política de cancelación de ${biz}, para que tus clientes confíen y puedan reservar`);
    parts.push('la cuenta bancaria donde TuSala transferirá, al finalizar cada mes, el dinero acumulado de tus reservas');
    return `Para dejar tu panel listo para operar, vamos a completar ${parts.join(' y también ')}.`;
  });
  step(){const base=this.currentSteps()[this.tourIndex()];if(base.module!=='estadisticas')return base;return this.isBasicPlan()?{...base,text:'Este módulo es exclusivo de Estándar y Premium. Escala tu plan para obtener reportes y análisis de ingresos y resultados.'}:{...base,text:'Analiza ingresos, reservas y rendimiento para tomar mejores decisiones.'}}
  refresh(afterRefresh?:()=>void){if(!this.businessId)return;this.setupStateReady.set(false);this.miSitioVisited.set(localStorage.getItem(this.key('setup-visited:mi-sitio'))==='1');this.tourCompleted.set(this.tourDone());this.onboardingGuideFinished.set(localStorage.getItem(this.key('onboarding-guide-finished'))==='1');this.tourOfferPending.set(localStorage.getItem(this.key('tour-offer-pending'))==='1');forkJoin({profile:this.business.obtenerPerfil(this.businessId),payouts:this.payouts.business(this.businessId),policies:this.policies.getPoliciesByBusiness(this.businessId,true)}).subscribe({next:({profile,payouts,policies})=>{
    const v:any=profile.business;
    this.hasCompanyInfo.set(!!(v.company?.trim()&&v.phone?.trim()));
    this.hasLogo.set(!!v.logo);
    this.hasActivePolicy.set(!!policies.privacyPolicies?.some((i:any)=>i.is_active));
    this.hasCancellationPolicy.set(!!v.cancellation_policy_configured_at);
    this.payoutComplete.set((payouts.methods||[]).length>0);
    this.noteWalkProgress();
    this.setupStateReady.set(true);afterRefresh?.();
    const pending=this.required||(this.firstLogin&&!this.setupSkipped()&&!this.tourDone());
    if(pending&&!this.setupDismissed&&!this.walkthroughActive()){this.showSetup.set(true);this.startIntroBubbles()}
  },error:()=>{this.setupStateReady.set(true);afterRefresh?.();const pending=this.required||(this.firstLogin&&!this.setupSkipped()&&!this.tourDone());if(pending&&!this.setupDismissed&&!this.walkthroughActive()){this.showSetup.set(true);this.startIntroBubbles()}}})}
  // Detecta, paso por paso, la transición de pendiente a completo (no en
  // la carga inicial si ya estaba listo de antes) para disparar el flash
  // de éxito correspondiente exactamente una vez por transición real. La
  // primera observación SOLO establece la línea base (nunca dispara nada):
  // sin esto, un propietario que ya tenía todo completo desde antes vería
  // flashes falsos de "¡Bien hecho!" apenas se monta el componente.
  private noteWalkProgress(){
    const relevantSteps=this.configurationRequired()?this.walkSteps():this.walkSteps().filter(s=>s.id==='payout');
    const allDoneNow=relevantSteps.every(s=>s.done());
    if(!this.stepTrackingInitialized){
      for(const step of this.walkSteps())this.prevStepDone[step.id]=step.done();
      this.prevAllDone=allDoneNow;
      this.stepTrackingInitialized=true;
      return;
    }
    for(const step of this.walkSteps()){
      const now=step.done();
      if(!this.prevStepDone[step.id]&&now&&step.flashMessage){
        this.justCompletedStep.set(step.id);
        if(this.stepFlashTimer)clearTimeout(this.stepFlashTimer);
        this.stepFlashTimer=window.setTimeout(()=>this.justCompletedStep.set(null),2600);
      }
      this.prevStepDone[step.id]=now;
    }
    // Transición real de "falta algo" -> "todo completo": si ya se había
    // decidido sobre el tour (o Kubo ya se había despedido antes y esto es
    // la corrección de una regresión), se cierra el flujo; si es la
    // primera vez que se completan los pasos guiados (vino de "Iniciar"),
    // recién ahí corresponde ofrecer el tour.
    if(allDoneNow&&!this.prevAllDone&&this.engagedWithOnboarding()){
      if(this.onboardingGuideFinished()||this.tourDecided()){
        this.concludeOnboarding(true);
      } else {
        // Ya interactuó (Iniciar u Omitir) y todavía no había decidido
        // sobre el tour -> este es el momento de ofrecérselo, sin importar
        // si vino del camino de "Iniciar" o de "Omitir".
        localStorage.setItem(this.key('tour-offer-pending'),'1');
        this.tourOfferPending.set(true);
      }
    }
    this.prevAllDone=allDoneNow;
  }
  // Bug real corregido: antes se asignaba window.location.hash ANTES de
  // emitir la navegación -- eso dispara hashchange/popstate, el router
  // vuelve a navegar a la URL de ese momento (p. ej. ?module=overview) y
  // cancela el cambio de módulo: "Ir" solo funcionaba si la URL aún no
  // tenía módulo. Ahora: en el mismo módulo solo se cambia la pestaña
  // (hashchange, que el módulo escucha); hacia otro módulo el hash se deja
  // sin eventos (replaceState) y la navegación lo lleva como fragmento.
  private goToModule(module:OnboardingModule,tabHash?:string):void{
    if(this.currentModule===module){if(tabHash&&window.location.hash.replace(/^#/,'')!==tabHash)window.location.hash=tabHash;return}
    history.replaceState(history.state,'',`${window.location.pathname}${window.location.search}${tabHash?'#'+tabHash:''}`);
    this.navigate.emit(module);
  }
  private goToStep(step:WalkStep):void{this.goToModule(step.module,step.tabHash);const t=this.stepTargets[step.id];this.kubo.guide(t,{title:t.title,text:t.text})}
  openMiSitio(){this.goToModule('configuracion','mi-sitio')}
  beginWalkthrough(){
    this.setupDismissed=true;
    this.showSetup.set(false);
    this.walkthroughActive.set(true);
    // Persistido (a diferencia de walkthroughActive/setupDismissed, que
    // son solo en memoria): si el usuario recarga a medio completar los
    // pasos, engagedWithOnboarding() debe seguir reconociendo que ya
    // decidió "Iniciar" en una sesión anterior.
    localStorage.setItem(this.key('walkthrough-started'),'1');
    this.stopIntroBubbles();
    // Se marca como "comprometido" apenas confirma que quiere empezar (no
    // al terminar el recorrido guiado) — si cierra la pestaña a medias, el
    // panel no debe volver a forzar el modal en cada sesión futura. Ver
    // razonamiento igual de startTour() más abajo.
    this.auth.completeOnboarding().subscribe({error:err=>console.error('No se pudo registrar el onboarding como completado:',err)});
    const step=this.currentWalkStep();
    if(step)this.goToStep(step);
  }
  skipSetup(){
    this.setupDismissed=true;
    localStorage.setItem(this.key('setup-skipped'),'1');
    this.showSetup.set(false);
    this.stopIntroBubbles();
    this.auth.completeOnboarding().subscribe({error:err=>console.error('No se pudo registrar el onboarding como completado:',err)});
    // Al omitir, en vez de no hacer nada más, Kubo ofrece de inmediato el
    // recorrido guiado (con opción de omitirlo también) — pedido explícito:
    // "si omite esas acciones automáticamente debe aparecer kubo indicando
    // que iniciará un recorrido".
    localStorage.setItem(this.key('tour-offer-pending'),'1');
    this.tourOfferPending.set(true);
  }
  startTour(initial=false){this.initialTour=initial;this.showSetup.set(false);this.stopIntroBubbles();this.tourIndex.set(0);this.displayedModule=undefined;this.tourActive.set(true);this.showCurrentStep()} previous(){if(!this.tourIndex())return;this.tourIndex.update(v=>v-1);this.showCurrentStep()} next(){if(this.tourIndex()>=this.currentSteps().length-1){this.finishTour(true);return}this.tourIndex.update(v=>v+1);this.showCurrentStep()}
  finishTour(done:boolean){
    this.tourActive.set(false);
    this.displayedModule=undefined;
    if(!done)return;
    localStorage.setItem(this.key('tour-done'),'1');
    this.tourCompleted.set(true);
    const wasInitial=this.initialTour;
    const showWelcome=wasInitial&&localStorage.getItem(this.key('welcome-shown'))!=='1';
    this.initialTour=false;
    const afterWelcome=()=>{if(wasInitial&&this.allComplete())this.concludeOnboarding(false)};
    if(showWelcome){
      localStorage.setItem(this.key('welcome-shown'),'1');
      this.welcome.set(true);
      window.setTimeout(()=>{this.welcome.set(false);afterWelcome()},2600);
    } else {
      afterWelcome();
    }
  }
  // Decisión sobre la oferta de recorrido (ver tourOfferRecommendation) —
  // ambas ramas quedan registradas como "ya decidido" para que, si más
  // adelante se terminan de completar los módulos pendientes, no se vuelva
  // a ofrecer el tour una segunda vez (ver noteWalkProgress()).
  private acceptTourOffer(){
    localStorage.setItem(this.key('tour-offer-pending'),'0');
    this.tourOfferPending.set(false);
    localStorage.setItem(this.key('tour-offer-decided'),'1');
    this.startTour(true);
  }
  private declineTourOffer(){
    localStorage.setItem(this.key('tour-offer-pending'),'0');
    this.tourOfferPending.set(false);
    localStorage.setItem(this.key('tour-offer-decided'),'1');
    const showWelcome=localStorage.getItem(this.key('welcome-shown'))!=='1';
    const afterWelcome=()=>{if(this.allComplete())this.concludeOnboarding(false)};
    if(showWelcome){
      localStorage.setItem(this.key('welcome-shown'),'1');
      this.welcome.set(true);
      window.setTimeout(()=>{this.welcome.set(false);afterWelcome()},2600);
    } else {
      afterWelcome();
    }
  }
  // Cierre del flujo: si withChecklist es true, primero confirma lo
  // completado (con un pequeño estado de carga) y recién después se
  // despide — si es false (ya se sabía que todo estaba completo, sin
  // haber pasado por el recordatorio persistente) va directo a la
  // despedida. En ambos casos, termina marcando onboardingGuideFinished,
  // que puede volver a false más adelante si se detecta una regresión
  // (ver kuboVisible()) — por eso esta misma función se reutiliza tanto
  // para el cierre inicial como para el cierre tras corregir una
  // regresión.
  private concludeOnboarding(withChecklist:boolean){
    this.walkthroughActive.set(false);
    if(this.finishStageTimer)clearTimeout(this.finishStageTimer);
    const goToGoodbye=()=>{
      this.finishingUpStage.set('goodbye');
      this.finishStageTimer=window.setTimeout(()=>{
        this.finishingUpStage.set(null);
        localStorage.setItem(this.key('onboarding-guide-finished'),'1');
        this.onboardingGuideFinished.set(true);
      },2800);
    };
    if(withChecklist){
      this.finishingUpStage.set('checklist');
      this.finishStageTimer=window.setTimeout(goToGoodbye,2800);
    } else {
      goToGoodbye();
    }
  }
  private showCurrentStep(){const module=this.step().module;if(this.displayedModule===module)return;this.displayedModule=module;this.navigate.emit(module)}
  currentSteps(){return this.role==='super_admin'?this.superAdminSteps:this.steps} isEligible(){return this.role==='owner'||this.role==='super_admin'} private isBasicPlan(){return /basico|básico/i.test(this.plan)} private key(v:string){return `tusala:${v}:${this.userId||this.businessId}`} private setupSkipped(){return localStorage.getItem(this.key('setup-skipped'))==='1'} private tourDone(){return localStorage.getItem(this.key('tour-done'))==='1'} private tourDecided(){return localStorage.getItem(this.key('tour-offer-decided'))==='1'} private walkthroughStarted(){return localStorage.getItem(this.key('walkthrough-started'))==='1'}
}
